"use client";

import { GameCard } from "@/components/feature/game-card";
import { PointerInteractionAutoProvider } from "@/components/feature/pointer-interaction-media";
import { PreviewIntentAutoProvider } from "@/components/feature/preview-intent";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { prepareGamesForCatalogGrid } from "@/lib/grid-icon-sizes";
import type { PageOriginContext } from "@/lib/page-origin-context";
import { CATALOG_GRID_MOBILE_MEDIA, clampShowcaseGridSpan } from "@/lib/showcase-grid/dimensions";
import type { GameWithRelations } from "@/types/game";
import { GameSize } from "@/types/game";
import "@/components/feature/masonry.css"; // Import masonry styles

interface GamesGridProps {
  games: GameWithRelations[];
  emptyMessage?: string;
  emptyDescription?: string;
  pageOrigin?: PageOriginContext | null;
  /** Forwarded to each GameCard Link (search results should pass false). */
  linkPrefetch?: boolean;
  /**
   * Serializable card-destination prefix (e.g. "/testgames"); cards link to
   * `${prefix}/${slug}`. A string so Server Components can pass it across the
   * RSC boundary — function props are not serializable.
   */
  gameHrefPrefix?: string;
}

export function GamesGrid({
  games,
  emptyMessage = "No Games Available",
  emptyDescription = "Check back soon for new games!",
  pageOrigin,
  linkPrefetch = true,
  gameHrefPrefix,
}: GamesGridProps) {
  const isMobileGrid = useMediaQuery(CATALOG_GRID_MOBILE_MEDIA);
  const uiSizeMap: Record<GameSize, "small" | "medium" | "large"> = {
    [GameSize.Small]: "small",
    [GameSize.Medium]: "medium",
    [GameSize.Large]: "large",
  };

  if (games.length === 0) {
    return (
      <div className="bg-white/10 p-8 rounded-2xl text-center">
        <h2 className="text-xl font-bold mb-2">{emptyMessage}</h2>
        <p>{emptyDescription}</p>
      </div>
    );
  }

  const sortedGames = prepareGamesForCatalogGrid(games);

  return (
    <PreviewIntentAutoProvider>
      <PointerInteractionAutoProvider>
        <div className="games-pinterest-grid">
          {sortedGames.map((game: GameWithRelations) => {
            const gameSize = (game.size ?? GameSize.Small) as GameSize;
            const displaySize = isMobileGrid
              ? (clampShowcaseGridSpan(gameSize) as GameSize)
              : gameSize;
            return (
              <div key={game.id} className={`grid-item size-${gameSize}`}>
                <GameCard
                  game={{ ...game, size: displaySize }}
                  size={uiSizeMap[displaySize]}
                  pageOrigin={pageOrigin}
                  linkPrefetch={linkPrefetch}
                  href={gameHrefPrefix ? `${gameHrefPrefix}/${game.slug}` : undefined}
                />
              </div>
            );
          })}
        </div>
      </PointerInteractionAutoProvider>
    </PreviewIntentAutoProvider>
  );
}
